import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  CreditCard,
  Coins,
  Loader2,
  ShoppingCart,
  CheckCircle,
  ChevronUp,
  ChevronDown,
  ArrowLeftRight,
  Users,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { formatDate, formatAuditTimestamp } from "@/lib/dateUtils";
import { toast } from "sonner";
import { PaymentStatus, PaymentPurpose } from "@/types/payment";
import type { CreditPackage } from "@/types/payment";
import type { SeatPlan } from "@/types/seats";
import {
  useCreditPackagesQuery,
  usePaymentsQuery,
  useCreateCheckoutMutation,
  useCancelPaymentMutation,
  useTransferTargetsQuery,
  useTransferCreditsMutation,
} from "@/hooks/queries/usePaymentsQuery";
import { useSeatPlansQuery, useCreateSeatCheckoutMutation } from "@/hooks/queries/useSeatsQuery";

interface PaymentsTabProps {
  organizationId: number;
  canEdit: boolean;
  canTransfer?: boolean;
  creditBalance: number;
  lastCreditsUpdatedAt?: string | null;
}

const PAGE_SIZE = 20;

const PaymentsTab = ({ organizationId, canEdit, canTransfer = false, creditBalance, lastCreditsUpdatedAt }: PaymentsTabProps) => {
  const { language } = useLanguage();
  const tz = useUserTimezone();
  const { t } = useTranslation(language);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [showSeatPlanDialog, setShowSeatPlanDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState<string>("");
  const [transferAmount, setTransferAmount] = useState<string>("");

  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const { data: packages = [], isLoading: loadingPackages } = useCreditPackagesQuery(organizationId);
  const { data: paymentsData, isLoading: loadingPayments } = usePaymentsQuery(
    organizationId,
    page,
    PAGE_SIZE,
    sortBy,
    sortDirection
  );
  const checkoutMutation = useCreateCheckoutMutation();
  const cancelMutation = useCancelPaymentMutation();
  const transferMutation = useTransferCreditsMutation();
  const { data: seatPlans = [], isLoading: loadingSeatPlans } = useSeatPlansQuery();
  const seatCheckoutMutation = useCreateSeatCheckoutMutation();
  const { data: transferTargets = [], isLoading: loadingTargets } = useTransferTargetsQuery(
    organizationId,
    showTransferDialog
  );

  const payments = paymentsData?.items ?? [];
  const totalPages = paymentsData?.totalPages ?? 1;
  const totalCount = paymentsData?.totalCount ?? 0;

  // Handle checkout success/cancel from URL params
  useEffect(() => {
    const checkoutParam = searchParams.get("checkout");
    if (checkoutParam === "success") {
      toast.success(t("checkoutSuccess"));
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
    } else if (checkoutParam === "cancel") {
      const sessionId = searchParams.get("session_id");
      if (sessionId) {
        cancelMutation.mutate({ organizationId, sessionId });
      }
      toast.info(t("checkoutCancel"));
      searchParams.delete("checkout");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  const handleBuyCredits = (pkg: CreditPackage) => {
    setShowPackageDialog(false);
    checkoutMutation.mutate({ organizationId, creditPackageId: pkg.id });
  };

  const handleBuySeats = (plan: SeatPlan) => {
    setShowSeatPlanDialog(false);
    seatCheckoutMutation.mutate({ organizationId, seatPlanId: plan.id });
  };

  const parsedTransferAmount = Number.parseFloat(transferAmount);
  const isTransferAmountValid =
    transferAmount.trim() !== "" &&
    Number.isFinite(parsedTransferAmount) &&
    parsedTransferAmount > 0 &&
    parsedTransferAmount <= creditBalance;
  const isTransferValid = transferTargetId !== "" && isTransferAmountValid;

  const openTransferDialog = (open: boolean) => {
    setShowTransferDialog(open);
    if (!open) {
      setTransferTargetId("");
      setTransferAmount("");
    }
  };

  const handleTransfer = () => {
    if (!isTransferValid) return;
    transferMutation.mutate(
      {
        organizationId,
        targetOrganizationId: Number(transferTargetId),
        amount: Math.round(parsedTransferAmount * 100) / 100,
      },
      {
        onSuccess: () => {
          toast.success(t("transferSuccess"));
          openTransferDialog(false);
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : t("transferError"));
        },
      }
    );
  };

  const handleSort = (column: string) => {
    if (column === sortBy) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDirection("desc");
    }
    setPage(1);
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (column !== sortBy) return null;
    return sortDirection === "desc"
      ? <ChevronDown className="ml-1 h-3 w-3 inline" />
      : <ChevronUp className="ml-1 h-3 w-3 inline" />;
  };

  const renderPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("ellipsis");
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("ellipsis");
      pages.push(totalPages);
    }
    return pages.map((p, i) =>
      p === "ellipsis" ? (
        <PaginationItem key={`ellipsis-${i}`}>
          <PaginationEllipsis />
        </PaginationItem>
      ) : (
        <PaginationItem key={p}>
          <PaginationLink
            isActive={p === page}
            onClick={() => setPage(p as number)}
            className="cursor-pointer"
          >
            {p}
          </PaginationLink>
        </PaginationItem>
      )
    );
  };

  const formatCurrency = (amountCents: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amountCents / 100);
  };

  const getStatusBadge = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.Pending:
        return <Badge variant="secondary">{t("paymentPending")}</Badge>;
      case PaymentStatus.Completed:
        return <Badge className="bg-green-500 hover:bg-green-600">{t("paymentCompleted")}</Badge>;
      case PaymentStatus.Failed:
        return <Badge variant="destructive">{t("paymentFailed")}</Badge>;
      case PaymentStatus.Refunded:
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">{t("paymentRefunded")}</Badge>;
      case PaymentStatus.Cancelled:
        return <Badge variant="outline">{t("paymentCancelled")}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPurposeBadge = (purpose: PaymentPurpose) => {
    switch (purpose) {
      case PaymentPurpose.SeatPurchase:
        return <Badge variant="outline" className="gap-1"><Users className="h-3 w-3" />{t("paymentTypeSeats")}</Badge>;
      case PaymentPurpose.CreditTransfer:
      case PaymentPurpose.CreditPurchase:
      default:
        return <Badge variant="outline" className="gap-1"><Coins className="h-3 w-3" />{t("paymentTypeCredits")}</Badge>;
    }
  };

  const lastCreditsDate = lastCreditsUpdatedAt ? new Date(lastCreditsUpdatedAt) : null;
  const hasValidLastCreditsDate = Boolean(lastCreditsDate && !Number.isNaN(lastCreditsDate.getTime()));
  const creditsExpireAt = hasValidLastCreditsDate
    ? new Date(
        lastCreditsDate!.getFullYear() + 1,
        lastCreditsDate!.getMonth(),
        lastCreditsDate!.getDate()
      )
    : null;
  const msUntilExpiry = creditsExpireAt ? creditsExpireAt.getTime() - Date.now() : null;
  const isExpiringSoon = msUntilExpiry !== null && msUntilExpiry >= 0 && msUntilExpiry <= 30 * 24 * 60 * 60 * 1000;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Coins size={14} />
            {t("organizationCreditBalance")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold">{creditBalance.toFixed(2)}</p>
          <p
            className={cn(
              "mt-2 text-sm text-muted-foreground",
              isExpiringSoon && "text-destructive"
            )}
          >
            {creditsExpireAt
              ? `${t("creditsUsableUntil")} ${formatDate(creditsExpireAt.toISOString(), tz, language)}`
              : t("creditsUsableUntilUnavailable")}
          </p>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {(canEdit || canTransfer) && (
        <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
          {canTransfer && (
            <Button
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => openTransferDialog(true)}
              disabled={transferMutation.isPending}
            >
              {transferMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowLeftRight className="mr-2 h-4 w-4" />
              )}
              {t("transferCredits")}
            </Button>
          )}
          {canEdit && (
            <Button className="w-full sm:w-auto" onClick={() => setShowPackageDialog(true)} disabled={checkoutMutation.isPending}>
              {checkoutMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ShoppingCart className="mr-2 h-4 w-4" />
              )}
              {t("buyCredits")}
            </Button>
          )}
          {canEdit && (
            <Button
              variant="default"
              className="w-full sm:w-auto"
              onClick={() => setShowSeatPlanDialog(true)}
              disabled={seatCheckoutMutation.isPending}
            >
              {seatCheckoutMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Users className="mr-2 h-4 w-4" />
              )}
              {t("buySeats")}
            </Button>
          )}
        </div>
      )}

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard size={18} />
            {t("paymentHistory")}
            {totalCount > 0 && (
              <span className="text-sm font-normal text-muted-foreground">({totalCount})</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingPayments ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : payments.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t("noPayments")}</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-muted/50"
                      onClick={() => handleSort("date")}
                    >
                      {t("paymentDate")}
                      <SortIcon column="date" />
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-muted/50"
                      onClick={() => handleSort("credits")}
                    >
                      {t("paymentCredits")}
                      <SortIcon column="credits" />
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-muted/50"
                      onClick={() => handleSort("amount")}
                    >
                      {t("paymentAmount")}
                      <SortIcon column="amount" />
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-muted/50"
                      onClick={() => handleSort("status")}
                    >
                      {t("paymentStatus")}
                      <SortIcon column="status" />
                    </TableHead>
                    <TableHead>{t("paymentType")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {formatAuditTimestamp(payment.createdAt, tz)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "font-medium",
                          payment.credits < 0 && "text-red-500",
                          payment.status === PaymentStatus.Cancelled && "line-through"
                        )}
                      >
                        {payment.credits.toFixed(0)} {t("credits")}
                      </TableCell>
                      <TableCell
                        className={cn(payment.status === PaymentStatus.Cancelled && "line-through")}
                        aria-label={
                          payment.credits < 0 ? "Amount intentionally empty for credit withdrawal" : undefined
                        }
                      >
                        {payment.credits < 0 ? "\u00A0" : formatCurrency(payment.amountPaid, payment.currency)}
                      </TableCell>
                      <TableCell>{getStatusBadge(payment.status)}</TableCell>
                      <TableCell>{getPurposeBadge(payment.paymentPurpose)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <Pagination className="mt-4">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className={page <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    {renderPageNumbers()}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        className={
                          page >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Credit Packages Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("creditPackages")}</DialogTitle>
            <DialogDescription>{t("selectPackage")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            {loadingPackages ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              packages.map((pkg) => (
                <Card
                  key={pkg.id}
                  className="cursor-pointer transition-colors hover:bg-accent"
                  onClick={() => handleBuyCredits(pkg)}
                >
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-semibold">{pkg.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {pkg.credits.toFixed(0)} {t("credits")}
                        </p>
                      </div>
                    </div>
                    <p className="text-lg font-bold">
                      {formatCurrency(pkg.priceAmount, pkg.priceCurrency)}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Seat Plans Dialog */}
      <Dialog open={showSeatPlanDialog} onOpenChange={setShowSeatPlanDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("seatPlans")}</DialogTitle>
            <DialogDescription>{t("selectSeatPlan")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-4">
            {loadingSeatPlans ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              seatPlans.map((plan) => (
                <Card
                  key={plan.id}
                  className="cursor-pointer transition-colors hover:bg-accent"
                  onClick={() => handleBuySeats(plan)}
                >
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-semibold">{plan.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {plan.seatCount} {t("seats")}
                          {plan.credits > 0 && ` · +${plan.credits.toLocaleString()} ${t("seatPlanBonusCredits")}`}
                        </p>
                      </div>
                    </div>
                    <p className="text-lg font-bold">
                      {formatCurrency(plan.priceAmount, plan.priceCurrency)}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Credits Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={openTransferDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("transferCreditsTitle")}</DialogTitle>
            <DialogDescription>{t("transferCreditsDescription")}</DialogDescription>
          </DialogHeader>

          {loadingTargets ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : transferTargets.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t("transferNoTargets")}</p>
          ) : (
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="transfer-target">{t("transferTargetOrganization")}</Label>
                <Select value={transferTargetId} onValueChange={setTransferTargetId}>
                  <SelectTrigger id="transfer-target">
                    <SelectValue placeholder={t("transferSelectTarget")} />
                  </SelectTrigger>
                  <SelectContent>
                    {transferTargets.map((target) => (
                      <SelectItem key={target.id} value={String(target.id)}>
                        {target.name} ({target.creditBalance.toFixed(2)} {t("credits")})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="transfer-amount">{t("transferAmount")}</Label>
                <Input
                  id="transfer-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={creditBalance}
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t("organizationCreditBalance")}: {creditBalance.toFixed(2)}
                </p>
                {transferAmount.trim() !== "" && !isTransferAmountValid && (
                  <p className="text-xs text-destructive">{t("transferInsufficientCredits")}</p>
                )}
              </div>
            </div>
          )}

          {transferTargets.length > 0 && (
            <DialogFooter>
              <Button
                onClick={handleTransfer}
                disabled={!isTransferValid || transferMutation.isPending}
              >
                {transferMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowLeftRight className="mr-2 h-4 w-4" />
                )}
                {t("transferSend")}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentsTab;
